import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { numberingConfigService } from '../../api/services';
import { NumberingConfig, TYPE_DOCUMENT_LABELS } from '../../types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/** Mirror of backend DocumentNumberFormatter — render preview client-side. */
function renderFormat(format: string, prefix: string, year: number, seq: number): string {
  let out = (format || '').trim() || '{PREFIX}-{YEAR}-{SEQ:00000}';
  out = out.replace(/\{PREFIX\}/g, prefix || '');
  out = out.replace(/\{YEAR\}/g, String(year));
  out = out.replace(/\{YYYY\}/g, String(year));
  out = out.replace(/\{YY\}/g, String(year).slice(-2).padStart(2, '0'));
  out = out.replace(/\{SEQ:(0+)\}/g, (_m, zeros: string) =>
    String(seq).padStart(zeros.length, '0'));
  out = out.replace(/\{SEQ\}/g, String(seq));
  return out;
}

const Numbering: React.FC = () => {
  const [configs, setConfigs] = useState<NumberingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setConfigs(await numberingConfigService.getAll());
    } catch {
      toast.error('Impossible de charger la numérotation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (id: number, updates: Partial<NumberingConfig>) => {
    setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  };

  const save = async (c: NumberingConfig) => {
    try {
      setSavingId(c.id);
      const updated = await numberingConfigService.update(c.id, {
        prefix: c.prefix,
        format: c.format,
        startAt: c.startAt,
        resetYearly: c.resetYearly,
      });
      setConfigs((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
      toast.success(`Numérotation « ${TYPE_DOCUMENT_LABELS[c.typeDocument]} » mise à jour`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  const year = new Date().getFullYear();

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold">Numérotation des documents</h2>
        <p className="text-sm text-gray-500">
          Personnalisez le préfixe, le format et la séquence de départ pour chaque
          type de document. Placeholders disponibles dans le format :{' '}
          <code className="bg-gray-100 px-1">{'{PREFIX}'}</code>,{' '}
          <code className="bg-gray-100 px-1">{'{YEAR}'}</code>,{' '}
          <code className="bg-gray-100 px-1">{'{YY}'}</code>,{' '}
          <code className="bg-gray-100 px-1">{'{SEQ}'}</code>,{' '}
          <code className="bg-gray-100 px-1">{'{SEQ:0000}'}</code>.
        </p>
      </div>

      <div className="space-y-4">
        {configs.map((c) => {
          const nextSeq = Math.max(c.currentNumber + 1, c.startAt || 1);
          const preview = renderFormat(c.format, c.prefix, year, nextSeq);
          return (
            <div key={c.id} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">{TYPE_DOCUMENT_LABELS[c.typeDocument]}</h3>
                <span className="text-xs text-gray-500">
                  Dernier numéro {year} :{' '}
                  <span className="font-mono">{c.currentNumber}</span>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="label">Préfixe</label>
                  <input
                    type="text"
                    value={c.prefix}
                    onChange={(e) => patch(c.id, { prefix: e.target.value })}
                    className="input-field font-mono"
                    maxLength={16}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Format</label>
                  <input
                    type="text"
                    value={c.format}
                    onChange={(e) => patch(c.id, { format: e.target.value })}
                    className="input-field font-mono"
                    maxLength={64}
                  />
                </div>
                <div>
                  <label className="label">Séquence de départ</label>
                  <input
                    type="number"
                    min={1}
                    value={c.startAt}
                    onChange={(e) => patch(c.id, { startAt: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    className="input-field font-mono"
                  />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={c.resetYearly}
                    onChange={(e) => patch(c.id, { resetYearly: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  Réinitialiser la séquence chaque année
                </label>

                <div className="text-sm">
                  <span className="text-gray-500">Aperçu : </span>
                  <span className="font-mono font-medium text-primary-700">{preview}</span>
                </div>

                <button
                  type="button"
                  onClick={() => save(c)}
                  disabled={savingId === c.id}
                  className="btn-primary"
                >
                  {savingId === c.id ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Numbering;
